import {unref} from 'vue';
import {defineNuxtRouteMiddleware, navigateTo, useLocale, useLocalePrefix, useNuxtApp, useRuntimeConfig} from '#imports';
import {name} from '../common';

function useSessionLoggedIn(): boolean {
    return unref(useNuxtApp().$session?.loggedIn) ?? false;
}

/**
 * Returns true when the path contains a locale prefix that should be stripped.
 */
// eslint-disable-next-line @malt/consistent-composable-naming
export function shouldStripLocalePrefix(to: {path: string}): boolean {
    const {hasLocalePrefix} = useLocalePrefix();
    if (!hasLocalePrefix(to.path)) {
        return false;
    }

    const {isMultiLocaleHost} = useLocale();
    if (!isMultiLocaleHost.value) {
        return true; // localised host
    }

    const i18nConfig = useRuntimeConfig().public[name];
    if (!i18nConfig?.enableLocalePrefixStrategy) {
        return true;
    }

    return !!i18nConfig?.stripLocalePrefixWhenLoggedIn && useSessionLoggedIn();
}

/**
 * Strips locale prefixes from URLs and redirects to the equivalent path without the prefix.
 */
export default defineNuxtRouteMiddleware((to) => {
    if (!shouldStripLocalePrefix(to)) {
        return;
    }

    const {withoutLocalePrefix} = useLocalePrefix();
    return navigateTo(
        {
            path: withoutLocalePrefix(to.path) as string,
            query: to.query,
            hash: to.hash,
        },
        {redirectCode: 302, replace: true},
    );
});
